import { useEffect, useState } from 'react';
import axios from 'axios';
import { API_BASE_URL } from '../config/ApiConfig';
import { useNavigate } from 'react-router-dom';
import PageCard from './components/PageCard';
import DefaultModal from './components/modals/DefaultModal';
import LoadingModal from './components/modals/LoadingModal';
import SaldoInvestimentoModal from './components/modalServices/SaldoInvestimentoModal';
import Pagination10 from './components/Pagination10';
import usePermissions from './components/hooks/usePermissions';
import { formatCurrencyView } from './utils/currencyUtils';
import useProgramaSituacao from './components/hooks/useProgramaSituacao';

function SaldosInvestimento() {
  // Verificação de permissões
  const { podeAbrir, podeCriar, podeEditar, podeExcluir, podeExportar, carregando: carregandoPermissoes } = usePermissions('saldos_investimento');
  
  // Verificação de situação do programa
  const { isProgramaAtivo } = useProgramaSituacao();
  
  const [saldos, setSaldos] = useState([]);
  const [atualizar, setAtualizar] = useState(0);
  const token = localStorage.getItem('token');
  const cnpj = localStorage.getItem('cnpj');
  const anoBase =
    parseInt(localStorage.getItem('anoBase')) || new Date().getFullYear();
  const [isLoading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [showSaldoModal, setShowSaldoModal] = useState(false);
  const [saldoToEdit, setSaldoToEdit] = useState(null);
  const [filtroGeral, setFiltroGeral] = useState('');
  const [isProgramaInativo, setIsProgramaInativo] = useState(false);

  useEffect(() => {
    if (!token) {
      navigate('/');
    }
    
    // Não carregar dados se não pode abrir o módulo
    if (!carregandoPermissoes && !podeAbrir) {
      return;
    }
    
    setLoading(true);
    axios
      .get(`${API_BASE_URL.SALDO_INVESTIMENTO}/all`, {
        params: { cnpjAssociacao: cnpj, ano: parseInt(anoBase) },
        headers: { Authorization: `Bearer ${token}` },
      })
      .then(result => {
        setSaldos(Array.isArray(result.data) ? result.data : []);
      })
      .catch(() => setSaldos([]))
      .finally(() => setLoading(false));
  }, [atualizar, token, cnpj, anoBase, navigate, podeAbrir, carregandoPermissoes]);

  const saldosFiltrados = saldos.filter(saldo => {
    if (!filtroGeral) return true;
    const busca = filtroGeral.toUpperCase();
    return `${saldo.ano} ${saldo.numConta} ${saldo.nomePrograma || ''}`.toUpperCase().includes(busca);
  });

  const totalItems = saldosFiltrados.length;
  const [pagination, setPagination] = useState({
    startIndex: 0,
    rowsPerPage: 10,
    currentPage: 1,
    onGoToPage: page => {
      setPagination(prev => ({
        ...prev,
        currentPage: page,
        startIndex: (page - 1) * prev.rowsPerPage,
      }));
    },
  });

  const selectedSaldos = saldosFiltrados.slice(
    pagination.startIndex,
    pagination.startIndex + pagination.rowsPerPage
  );

  useEffect(() => {
    setPagination(prev => ({ ...prev, currentPage: 1, startIndex: 0 }));
  }, [filtroGeral]);

  const getMonthCellStyle = (value, isSelected) => {
    const baseStyle = isSelected
      ? { fontWeight: 'bold', color: '#0d6efd' }
      : {};
    if (value < 0) {
      return { ...baseStyle, color: '#dc3545', fontWeight: 'bold' };
    }
    return baseStyle;
  };

  const handleSelectRecord = record => {
    const recordKey = `${record.cnpjAssociacao}-${record.ano}-${record.numConta}-${record.idPrograma}`;
    if (selectedRecord && selectedRecord.key === recordKey) {
      setSelectedRecord(null);
      setIsProgramaInativo(false);
    } else {
      setSelectedRecord({ key: recordKey, data: record });
      // Verificar se o programa está encerrado (se houver idPrograma)
      if (record.idPrograma) {
        const programaAtivo = isProgramaAtivo(record.idPrograma);
        setIsProgramaInativo(!programaAtivo);
      } else {
        setIsProgramaInativo(false);
      }
    }
  };

  const handleNewSaldo = () => {
    setSaldoToEdit(null);
    setTimeout(() => setShowSaldoModal(true), 0);
  };

  const handleEditSaldo = () => {
    if (selectedRecord) {
      setSaldoToEdit(selectedRecord.data);
      setShowSaldoModal(true);
    }
  };

  const handleCloseSaldoModal = () => {
    setShowSaldoModal(false);
    setSaldoToEdit(null);
    setSelectedRecord(null);
  };

  // Verificar se ainda está carregando permissões
  if (carregandoPermissoes || isLoading) {
    return <LoadingModal loading={true} />;
  }

  return (
      <PageCard
        title="Saldos de Investimento"
        icon="bi-piggy-bank"
        scrollable={false}
        footer={
          totalItems > 0 ? (
            <Pagination10
              startIndex={pagination.startIndex}
              rowsPerPage={pagination.rowsPerPage}
              currentPage={pagination.currentPage}
              onGoToPage={pagination.onGoToPage}
              totalItems={totalItems}
            />
          ) : null
        }
      >
        {!podeAbrir ? (
          <div className="alert alert-warning m-3">
            <i className="bi bi-exclamation-triangle me-2"></i>
            <strong>Acesso Negado</strong>
            <p className="mb-0 mt-2">
              Você não tem permissão para acessar o módulo de Saldos de Investimento.
            </p>
            <p className="mb-0 mt-2">
              <small>Entre em contato com o administrador do sistema.</small>
            </p>
            <button 
              className="btn btn-sm btn-outline-secondary mt-3"
              onClick={() => navigate('/home')}
            >
              <i className="bi bi-arrow-left me-1"></i>
              Voltar
            </button>
          </div>
        ) : (
        <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div className="d-flex flex-wrap gap-1 mb-2 p-2 bg-light rounded border align-items-center">
          <div className="d-flex align-items-center gap-1">
            <button
              onClick={handleNewSaldo}
              className="btn btn-success btn-sm d-inline-flex align-items-center"
              disabled={!podeCriar}
              title={!podeCriar ? 'Você não tem permissão para criar saldos de investimento' : 'Novo saldo de investimento'}
            >
              <i className="bi bi-plus-lg me-1" />
              Novo
            </button>
            <button
              onClick={handleEditSaldo}
              className="btn btn-primary btn-sm d-inline-flex align-items-center"
              disabled={!podeEditar || !selectedRecord || isProgramaInativo}
              title={!podeEditar ? 'Você não tem permissão para editar' : !selectedRecord ? 'Selecione um registro' : isProgramaInativo ? 'Programa ENCERRADO - não é possível editar' : 'Editar registro selecionado'}
            >
              <i className="bi bi-pencil me-1" />
              Editar
            </button>
          </div>

          <div className="vr mx-1" />

          {selectedRecord && (
            <div className="d-flex align-items-center px-2 py-1 bg-warning-subtle border border-warning rounded">
              <i className="bi bi-check-circle-fill text-warning me-2" />
              <small className="text-dark fw-medium">
                Selecionado:{' '}
                <span className="fw-bold">
                  Conta {selectedRecord.data.numConta} -{' '}
                  {selectedRecord.data.nomePrograma || 'Programa'}
                </span>
              </small>
            </div>
          )}

          <div className="ms-auto d-flex align-items-center">
            <div className="input-group" style={{ maxWidth: 300, height: 31 }}>
              <span className="input-group-text bg-white border-end-0 px-2" style={{ height: 31 }}>
                <i className="bi bi-search text-muted" style={{ fontSize: 13 }} />
              </span>
              <input
                type="text"
                className="form-control form-control-sm border-start-0"
                style={{ textTransform: 'uppercase', height: 31, fontSize: 13 }}
                placeholder="Pesquisar por ano/conta..."
                value={filtroGeral}
                onChange={e => setFiltroGeral(e.target.value.toUpperCase())}
              />
              {filtroGeral && (
                <button
                  className="btn btn-outline-secondary btn-sm border-start-0 d-flex align-items-center px-2"
                  style={{ height: 31 }}
                  onClick={() => setFiltroGeral('')}
                  title="Limpar pesquisa"
                  tabIndex={-1}
                >
                  <i className="bi bi-x-lg" style={{ fontSize: 11 }} />
                </button>
              )}
            </div>
          </div>
        </div>

        <div
          className="table-responsive-sm shadow-sm mb-0 small"
          style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}
        >
          <table className="table table-hover table-striped table-sm">
            <thead className="table-secondary sticky-top bg-light">
              <tr>
                <th className="px-2 align-middle">Ano</th>
                <th className="px-2 align-middle">Conta</th>
                <th className="px-2 align-middle">Programa</th>
                <th className="px-1 align-middle text-center">JAN</th>
                <th className="px-1 align-middle text-center">FEV</th>
                <th className="px-1 align-middle text-center">MAR</th>
                <th className="px-1 align-middle text-center">ABR</th>
                <th className="px-1 align-middle text-center">MAI</th>
                <th className="px-1 align-middle text-center">JUN</th>
                <th className="px-1 align-middle text-center">JUL</th>
                <th className="px-1 align-middle text-center">AGO</th>
                <th className="px-1 align-middle text-center">SET</th>
                <th className="px-1 align-middle text-center">OUT</th>
                <th className="px-1 align-middle text-center">NOV</th>
                <th className="px-1 align-middle text-center">DEZ</th>
              </tr>
            </thead>
            <tbody>
              {selectedSaldos.map(s => {
                  const recordKey = `${s.cnpjAssociacao}-${s.ano}-${s.numConta}-${s.idPrograma}`;
                  const isSelected =
                    selectedRecord && selectedRecord.key === recordKey;
                  return (
                    <tr
                      key={recordKey}
                      style={
                        isSelected
                          ? {
                              cursor: 'pointer',
                              backgroundColor: '#e3f2fd',
                              borderLeft: '4px solid #0d6efd',
                              boxShadow: '0 2px 4px rgba(13, 110, 253, 0.2)',
                            }
                          : { cursor: 'pointer' }
                      }
                      onClick={() => handleSelectRecord(s)}
                    >
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {s.ano}
                      </td>
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {s.numConta}
                      </td>
                      <td
                        style={
                          isSelected
                            ? { fontWeight: 'bold', color: '#0d6efd' }
                            : {}
                        }
                      >
                        {s.nomePrograma || '-'}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.janeiro, isSelected)}
                      >
                        {formatCurrencyView(s.janeiro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.fevereiro, isSelected)}
                      >
                        {formatCurrencyView(s.fevereiro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.marco, isSelected)}
                      >
                        {formatCurrencyView(s.marco)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.abril, isSelected)}
                      >
                        {formatCurrencyView(s.abril)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.maio, isSelected)}
                      >
                        {formatCurrencyView(s.maio)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.junho, isSelected)}
                      >
                        {formatCurrencyView(s.junho)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.julho, isSelected)}
                      >
                        {formatCurrencyView(s.julho)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.agosto, isSelected)}
                      >
                        {formatCurrencyView(s.agosto)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.setembro, isSelected)}
                      >
                        {formatCurrencyView(s.setembro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.outubro, isSelected)}
                      >
                        {formatCurrencyView(s.outubro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.novembro, isSelected)}
                      >
                        {formatCurrencyView(s.novembro)}
                      </td>
                      <td
                        className="text-end px-1"
                        style={getMonthCellStyle(s.dezembro, isSelected)}
                      >
                        {formatCurrencyView(s.dezembro)}
                      </td>
                    </tr>
                  );
                })}
              {saldosFiltrados.length === 0 && (
                <tr>
                  <td colSpan="15" className="text-center">
                    Nenhum saldo de investimento encontrado.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
        )}

      {showSaldoModal && (
        <SaldoInvestimentoModal
          show={showSaldoModal}
          onClose={handleCloseSaldoModal}
          saldo={saldoToEdit}
          onSaveSuccess={() => {
            handleCloseSaldoModal();
            setAtualizar(prev => prev + 1);
          }}
        />
      )}
      </PageCard>
  );
}

export default SaldosInvestimento;
